import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';

import { useWorkflowStackTrace } from '../../hooks/use-workflow-inspection';
import { isNotRunningError } from '../../utils/inspection-errors';

interface Props {
  workflowId: string;
  runId: string | null;
  isRunning: boolean;
}

/**
 * Call Stack (§3.7): where the workflow coroutine is parked right now. Only
 * exists while RUNNING, so a closed execution gets the explanation instead of
 * a request; a point-in-time snapshot refreshed on demand. 503 = no worker
 * answered.
 */
export function CallStackTab({ workflowId, runId, isRunning }: Props) {
  const { t } = useTranslation('workflow-inspection');
  const trace = useWorkflowStackTrace(workflowId, runId, isRunning);

  if (!isRunning || isNotRunningError(trace.error)) {
    return (
      <p
        className="text-muted-foreground rounded-lg border p-6 text-center text-sm"
        data-testid="workflow-inspector-call-stack-not-running"
      >
        {t('callStack.notRunning')}
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="workflow-inspector-call-stack">
      <div className="flex items-center justify-end gap-1">
        {trace.data && (
          <CopyIconButton text={trace.data.stackTrace} label={t('failure.stackTrace')} />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="cursor-pointer gap-1.5"
          isLoading={trace.isFetching}
          onClick={() => void trace.refetch()}
          data-testid="workflow-inspector-call-stack-refresh-button"
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          {t('callStack.refresh')}
        </Button>
      </div>
      {trace.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : trace.isError || !trace.data ? (
        <p
          className="text-muted-foreground rounded-lg border p-4 text-sm"
          data-testid="workflow-inspector-call-stack-error"
        >
          {t('callStack.unavailable')}
        </p>
      ) : (
        <pre
          data-testid="workflow-inspector-call-stack-trace"
          className="bg-muted/30 max-h-[28rem] overflow-auto rounded-lg border p-3 font-mono text-xs whitespace-pre"
        >
          {trace.data.stackTrace}
        </pre>
      )}
    </div>
  );
}
